import React, { lazy } from 'react'
import { Redirect, useParams } from 'react-router-dom'
import buildRoutes from './buildRoutes'
import { anyAuth, loggedOutAuth } from './authStates'
import env from '../env'
import { maRolesMap } from '../auth/roles'
import { withVirusTotalIndicatorRedirect } from './hocs/withVirusTotalIndicatorRedirect'

const AcceptInvite = lazy(() => import('./AcceptInvite'))
const AccountManagement = lazy(() => import('./AccountManagement'))
const ActorPage = lazy(() => import('./ActorPage'))
const ActorExplore = lazy(() => import('./ActorExplore'))
const Admin = lazy(() => import('./Admin/Admin'))
const AdminPromotedReports = lazy(() => import('./Admin/PromotedReports'))
const Campaign = lazy(() => import('./Campaign'))
const CampaignExplore = lazy(() => import('./CampaignExplore'))
const ConfirmAccount = lazy(() => import('./ConfirmAccount'))
const Dashboard = lazy(() => import('./Dashboard'))
const Indicator = lazy(() => import('./Indicator'))
const Malware = lazy(() => import('./Malware'))
const MalwareExplore = lazy(() => import('./MalwareExplore'))
const MitreAttackExplore = lazy(() => import('./MitreAttackExplore'))
const Organizations = lazy(() => import('./Admin/Organizations'))
const ReportsExplore = lazy(() => import('./ReportsExplore'))
const Reports = lazy(() => import('./Admin/Reports'))
const Upgrade = lazy(() => import('./Upgrade/Upgrade'))
const Users = lazy(() => import('./Admin/Users'))
const TokenRetrieval = lazy(() => import('./TokenRetrieval'))
const VulnerabilityExplore = lazy(() => import('./VulnerabilityExplore'))
const VulnerabilityPage = lazy(() => import('./VulnerabilityPage'))

export const routes = buildRoutes({
  acceptInvite: ['/onboard/:id', AcceptInvite, { authState: anyAuth }],
  accountManagement: ['/accountmanagement', AccountManagement],
  actorPage: ['/actors/:id', ActorPage],
  actorExplore: ['/actors', ActorExplore],
  admin: ['/admin', Admin, { exact: true, maAdminRoles: [maRolesMap.promotedReports, maRolesMap.mandiantAdmin, maRolesMap.mandiantApprover] }],
  campaign: ['/campaigns/:id', Campaign, { hasFeature: env?.FEATURE_CAMPAIGN, entitlements: ['INTEL_RBI_OPS', 'INTEL_RBI_FUS'] }],
  campaignExplore: ['/campaigns', CampaignExplore, { hasFeature: env?.FEATURE_CAMPAIGN, entitlements: ['INTEL_RBI_OPS', 'INTEL_RBI_FUS'] }],
  confirmAccount: ['/confirmation', ConfirmAccount, { authState: anyAuth }],
  cve: ['/cve/:id', VulnerabilityPage, { entitlements: ['INTEL_RBI_VULN', 'INTEL_RBI_FUS'] }],
  dashboard: ['/', Dashboard, { exact: true }],
  indicator: ['/indicator/:type/:value', withVirusTotalIndicatorRedirect(Indicator)],
  indicators: ['/indicators/:type/:value', () => {
    const { type, value } = useParams()
    return <Redirect to = {`/indicator/${type}/${value}`} />
  }],
  malware: ['/malware/:id', Malware],
  malwareExplore: ['/malware', MalwareExplore],
  mitreAttackExplore: ['/mitreattack', MitreAttackExplore],
  reportsExplore: ['/reports', ReportsExplore],
  tokenRetrieval: ['/auth/msso', TokenRetrieval, { authState: loggedOutAuth }],
  // old /tool route is being temporarily kept in place and rerouted to /malware
  tool: ['/tool/:id', () => {
    const { id } = useParams()
    return (<Redirect to={`/malware/${id}`} />)
  }],
  upgrade: ['/upgrade', Upgrade],
  vulnerability: ['/vulnerabilities/:id', VulnerabilityPage, { entitlements: ['INTEL_RBI_VULN', 'INTEL_RBI_FUS'] }],
  vulnerabilityExplore: ['/vulnerabilities', VulnerabilityExplore, { entitlements: ['INTEL_RBI_VULN', 'INTEL_RBI_FUS'] }],
  // Admin routes
  adminPromotedReports: ['/admin/promoted_intel', AdminPromotedReports, { maAdminRoles: [maRolesMap.promotedReports] }],
  organizations: ['/admin/organizations', Organizations, { maAdminRoles: [maRolesMap.mandiantAdmin] }],
  users: ['/admin/users', Users, { maAdminRoles: [maRolesMap.mandiantAdmin, maRolesMap.mandiantApprover] }],
  reporting: ['/admin/reporting', Reports, { maAdminRoles: [maRolesMap.mandiantAdmin] }],
})

export default routes
